import Box from "@mui/material/Box";
import { useEffect, useState } from "react";

import { StyledAnchor } from "@/components/common/styles";
import MarketingLottie from "@/components/marketing/MarketingLottie";
import {
  ACCENT_DEEP,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import { ArrowIcon } from "@/components/marketing/MarketingIcons";
import { STICKY_BAR } from "./homeContent";

/**
 * The published page reveals this bar off a 560px sentinel inside the products
 * section rather than a scroll offset, so the trigger point survives the page
 * getting taller. `ProductSection` draws the sentinel; this looks it up, which
 * keeps the two from having to pass a ref through the whole page.
 */
export const STICKY_TRIGGER_ID = "home-sticky-trigger";

/**
 * How far below the viewport the observer's root is stretched. It only has to
 * clear the tallest document the site serves, around 8,000px, so the sentinel
 * can never sit past the root's far edge.
 */
const ROOT_REACH_PX = 1_000_000;

/**
 * The fixed conversion bar. Hidden at rest below the fold — 80px of travel
 * clears its own 54px box — and out of the tab order with it, so focus cannot
 * land on a control nothing draws. Toggled, not latched: the published page runs
 *
 *   ScrollTrigger.create({ trigger: ".trigger-div", start: "top top",
 *     end: "bottom top", onLeave: show, onEnterBack: hide })
 *
 * so the bar appears once the sentinel has passed above the viewport and goes
 * away again on the way back up.
 */
export default function StickyFreeBar() {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const trigger = document.getElementById(STICKY_TRIGGER_ID);
    if (typeof IntersectionObserver === "undefined") {
      // Nothing to watch the sentinel with, so the bar comes up rather than
      // never: a conversion control that is merely early beats one that is gone.
      setRevealed(true);
      return;
    }
    if (!trigger) {
      return;
    }
    // The root reaches past the end of the document rather than stopping at the
    // viewport, so the sentinel has two states instead of three: its bottom
    // edge is above the viewport's top or it is not, which is the question
    // `end: "bottom top"` asks. Against the bare viewport it reads "not
    // intersecting" both below the fold and above it, so a scroll landing on
    // the far side without rendering a frame in between crossed no threshold
    // and delivered no callback, which is what left the bar hidden past its own
    // trigger point on anchor jumps, restored offsets and fast wheels. Still
    // one browser-computed observation per changed frame, with nothing reading
    // layout on the scroll itself.
    const observer = new IntersectionObserver(
      ([entry]) =>
        setRevealed(
          !entry.isIntersecting && entry.boundingClientRect.bottom <= 0
        ),
      { rootMargin: `0px 0px ${ROOT_REACH_PX}px 0px` }
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      inert={!revealed}
      sx={{
        position: "fixed",
        inset: "auto 0 64px",
        zIndex: 200,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        transition: "all 0.4s cubic-bezier(0.645, 0.045, 0.355, 1)",
        transform: revealed ? "none" : "translateY(80px)",
        // `all` carries `visibility`, which holds `visible` for the whole
        // transition and flips only at its end, so the bar still slides away.
        visibility: revealed ? "visible" : "hidden",
        [TINY]: { justifyContent: "flex-start", pl: "12px", pr: "56px" },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: "0 0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "444px",
          mx: "auto",
          height: "54px",
          p: "8px 8px 8px 1.6px",
          backgroundColor: "background.default",
          border: "1.3px solid rgba(79, 92, 101, 0.5)",
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(79, 92, 101, 0.5)",
          fontSize: 14,
          lineHeight: "21px",
          letterSpacing: "-0.02em",
          color: marketingPalette.ink,
          WebkitTextStrokeWidth: "0.4px",
          [TINY]: {
            inset: "0 auto auto 16px",
            mx: 0,
            pl: "8px",
            width: "302px",
            fontSize: 13,
            lineHeight: "17px",
          },
        }}
      >
        {/*
          32px, and 28 below 480 — the published bar narrows the gift there, and
          those 4px are what let the button keep its full width inside the 302px
          box rather than squeezing the copy further.
        */}
        <Box
          sx={{
            flex: "0 0 auto",
            width: "32px",
            height: "32px",
            [TINY]: { width: "28px" },
          }}
        >
          <MarketingLottie
            path={STICKY_BAR.lottie}
            loop
            play
            style={{ width: "100%", height: "100%" }}
          />
        </Box>
        {/*
          The published title carries no max-width above 480: the box is 444px,
          the copy measures 282.8px and both sentences sit on one line. The 17ch
          cap belongs only to the 302px box below 480, where the copy is meant to
          take two lines — applied at every width it forced the wrap everywhere.
        */}
        <Box sx={{ [TINY]: { maxWidth: "17ch" } }}>{STICKY_BAR.title}</Box>
        <StyledAnchor
          href={STICKY_BAR.cta.href}
          target="_blank"
          rel="noopener"
          sx={{
            flex: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4.8px",
            ml: "8px",
            height: "36px",
            p: "4px 8px",
            borderRadius: "6px",
            backgroundColor: ACCENT_DEEP,
            color: "common.white",
            fontSize: 12,
            lineHeight: "18px",
          }}
        >
          {STICKY_BAR.cta.label}
          {/*
            The published arrow is a 12px glyph in a 16px slot. Drawing the glyph
            alone leaves the button 4px narrow, which is the whole difference
            between its 106.97px and ours.
          */}
          <Box sx={{ display: "flex", flex: "0 0 auto", width: "16px" }}>
            <ArrowIcon size={12} />
          </Box>
        </StyledAnchor>
      </Box>
    </Box>
  );
}
